import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QURAN_SURAH, VersesInfo } from "../../classes/UserProgress";
import { motion } from "framer-motion";
import {
  fetchLatestUserProgress,
  fetchUserName,
} from "../../services/firebase";
import { GroupMember } from "../../classes/GroupMemberProgress";

interface GroupProgressCardProps {
  cardTitle: string;
  memberIds: string[];
}

export interface MemberProgress {
  id: string;
  memberInfo: GroupMember;
  progressFrom: VersesInfo;
  progressTo: VersesInfo;
}

const surahName = (chapter: number) => {
  return QURAN_SURAH.find((surah) => surah.number === chapter)?.name;
};

const getUserProgressForCard = (userProgress: MemberProgress) => {
  return {
    progressFrom: `Surah ${surahName(userProgress.progressFrom.chapter)} (${
      userProgress.progressFrom.chapter
    }:${userProgress.progressFrom.verse})`,
    progressTo: `Surah ${surahName(userProgress.progressTo.chapter)} (${
      userProgress.progressTo.chapter
    }:${userProgress.progressTo.verse})`,
  };
};

const GroupProgressCard: React.FC<GroupProgressCardProps> = ({
  cardTitle,
  // buttonLink,
  memberIds,
}) => {
  const navigate = useNavigate();

  const vistButtonLink = (link: string) => {
    navigate(link);
  };

  const getRandomColor = () => {
    const colors = [
      "#3DCE53",
      "#3DCECC",
      "#CE3D3D",
      "#CE3D9D",
      "#3D3DCE",
      "#9D3DCE",
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const [showAllProgress, setShowAllProgress] = useState(false);

  const [showComingSoon, setShowComingSoon] = useState(false);

  const handleClickBlur = () => {
    setShowComingSoon(true);
    audio.currentTime = 0; // Ensure the sound plays from the start
    audio.play();

    setTimeout(() => setShowComingSoon(false), 1500); // Hide after 1.5s
  };

  const [audio] = useState(new Audio("/transition-base-main.mp3"));

  useEffect(() => {
    audio.preload = "auto"; // Preload the audio file
  }, [audio]);

  const [membersProgress, setMembersProgress] = useState<MemberProgress[]>([]);

  useEffect(() => {
    if (cardTitle === "") {
      setShowAllProgress(true);
    }

    const fetchMembersLatestProgress = async () => {
      if (memberIds.length === 0) return;

      try {
        const results = await Promise.all(
          memberIds.map(async (memberId) => {
            const latestProgress = await fetchLatestUserProgress(memberId);
            if (!latestProgress) return null;

            const memberInfo = await fetchUserName(memberId);
            // console.log("userName: ", memberInfo);
            return {
              id: latestProgress.id,
              memberInfo: memberInfo,
              progressFrom: latestProgress.progressFrom,
              progressTo: latestProgress.progressTo,
            };
          })
        );

        const filteredResults = results.filter(
          (progress): progress is MemberProgress => progress !== null
        );

        setMembersProgress(filteredResults);
      } catch (error) {
        console.error("Error fetching member progress:", error);
      }
    };

    fetchMembersLatestProgress();
  }, [memberIds]);

  return (
    <>
      <div
        className={`${
          cardTitle === "" ? "" : "bg-white"
        } rounded-[2.8em] overflow-auto`}
        // onClick={handleClickBlur}
      >
        {/* Content with Blur Effect */}
        <div className="relative">
          <motion.div
            animate={
              showComingSoon ? { filter: "blur(8px)" } : { filter: "blur(0px)" }
            }
            transition={{ duration: 0.5 }}
            className="relative z-0"
          >
            <div
              className={`w-full ${
                cardTitle === "" ? "" : "px-6 pt-6"
              }  flex flex-col ${
                membersProgress.length <= 3 || showAllProgress ? "pb-11" : ""
              }`}
            >
              {cardTitle && (
                <h3 className="font-grgrifter text-[1.6em] tracking-wider text-[#1B1B1B] mt-2">
                  {cardTitle}
                </h3>
              )}

              {(showAllProgress
                ? membersProgress
                : membersProgress.slice(0, 3)
              ).map((memberProgress) => {
                const cardConfig = getUserProgressForCard(memberProgress);

                return (
                  <div
                    className="flex items-center mt-4"
                    key={memberProgress.id}
                  >
                    <div className="mr-6">
                      {memberProgress.memberInfo?.profilePicURL ? (
                        <img
                          className="w-[8em] rounded-full flex items-center justify-center "
                          src={memberProgress.memberInfo.profilePicURL}
                          alt=""
                        />
                      ) : (
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: getRandomColor() }}
                        >
                          <p className="font-grgrifter text-xl text-white opacity-50">
                            {memberProgress.memberInfo.userName
                              .charAt(0)
                              .toUpperCase()}
                          </p>
                        </div>
                      )}
                    </div>

                    <p className="pr-4">
                      {memberProgress.memberInfo.userName} Completed{" "}
                      <span className="font-bold">
                        {cardConfig.progressFrom}
                      </span>{" "}
                      to{" "}
                      <span className="font-bold">{cardConfig.progressTo}</span>
                    </p>
                  </div>
                );
              })}
            </div>

            {membersProgress.length > 3 && !showAllProgress && (
              <div className="flex justify-center w-full more-info-btn">
                <button
                  className="font-grgrifter text-[#3A9437]"
                  onClick={() => setShowAllProgress(true)}
                >
                  View All
                </button>
              </div>
            )}
          </motion.div>

          {/* {showComingSoon && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2  px-4 py-2 rounded-lg whitespace-nowrap"
            >
              <p className="text-[#1B1B1B] font-bold text-2xl">Coming Soon</p>
            </motion.div>
          )} */}
        </div>
      </div>
    </>
  );
};

export default GroupProgressCard;
